import React, { useState, useEffect, useMemo, useContext } from "react";
import axios from "axios";
import moment from "moment";
import Swal from "sweetalert2";
import ExportJsonExcel from "js-export-excel";
import { Input, Label, Spinner, Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { AuthContext } from "../../contexts/AuthContext";
import Header from "../../layout/Header/Header";
import { IcTicket, IcHandHoldingUsd, IcCheckCircle, IcBan, IcExcel, IcTable, IcPlus, IcEdit } from "../../Icons";

const BASE = process.env.REACT_APP_URL_LUGARES_FIESTAS;
const COLOR = "#8b5cf6";

const SECCIONES = [
  { key: "hombresRH", label: "Hombres RH", abbr: "Hom RH" },
  { key: "mujeresRH", label: "Mujeres RH", abbr: "Muj RH" },
  { key: "mujeresRHA", label: "Mujeres Afuera RH", abbr: "Muj RHA" },
  { key: "hombresKipur", label: "Hombres Kipur", abbr: "Hom YK" },
  { key: "mujeresKipur", label: "Mujeres Kipur", abbr: "Muj YK" },
  { key: "mujeresKipurA", label: "Mujeres Afuera Kipur", abbr: "Muj YKA" },
];

// Etiquetas legibles (el estado interno es camelCase)
const ESTADO_LABEL = {
  pagada: "Pagada TDC",
  pagadaEfectivo: "Pagada Efectivo",
  cortesia: "Cortesía",
  pendiente: "Pendiente",
  expirada: "Expirada",
  cancelada: "Cancelada",
};

const ESTADO_STYLE = {
  pagada: { bg: "#dcfce7", color: "#16a34a" },
  pagadaEfectivo: { bg: "#d1fae5", color: "#047857" },
  cortesia: { bg: "#e0e7ff", color: "#4f46e5" },
  pendiente: { bg: "#fef3c7", color: "#d97706" },
  expirada: { bg: "#f1f5f9", color: "#64748b" },
  cancelada: { bg: "#fee2e2", color: "#dc2626" },
};

const fmt = (centavos) => "$" + new Intl.NumberFormat("es-MX").format((centavos || 0) / 100);

function ListadoLugaresFiestas() {
  const { user } = useContext(AuthContext);
  const headers = { Authorization: `Bearer: ${localStorage.getItem("app_token")}` };

  const [reservas, setReservas] = useState([]);
  // Apartados en proceso de pago (aún no son reservas)
  const [apartados, setApartados] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);

  const [estado, setEstado] = useState("");
  const [inicio, setInicio] = useState("");
  const [fin, setFin] = useState("");
  const [q, setQ] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [limitesEdit, setLimitesEdit] = useState({});
  const [savingLimites, setSavingLimites] = useState(false);
  const [modalCupos, setModalCupos] = useState(false);
  const [verDesglose, setVerDesglose] = useState(false);

  // Modal: registrar reserva manual (admin)
  const [modalManual, setModalManual] = useState(false);
  const [savingManual, setSavingManual] = useState(false);
  const FORM_VACIO = {
    nombre: "", email: "", telefono: "", esAbrej: false, comentarios: "",
    estado: "pagada", totalPesos: "",
    lugares: SECCIONES.reduce((a, s) => ({ ...a, [s.key]: 0 }), {}),
  };
  const [fManual, setFManual] = useState(FORM_VACIO);

  // Modal: editar reserva existente
  const [modalEditar, setModalEditar] = useState(false);
  const [savingEditar, setSavingEditar] = useState(false);
  const [fEditar, setFEditar] = useState(null);

  // Listado con filtros (server-side)
  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: "500" });
    if (estado) params.append("estado", estado);
    if (inicio) params.append("inicio", inicio);
    if (fin) params.append("fin", fin);
    if (q.trim()) params.append("q", q.trim());
    axios
      .get(`${BASE}?${params.toString()}`, { headers })
      .then((r) => {
        setReservas(r.data.data || []);
        setApartados(r.data.apartados || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [estado, inicio, fin, q, refresh]); // eslint-disable-line

  // Resumen (corte por sección)
  useEffect(() => {
    axios.get(`${BASE}/resumen`, { headers })
      .then((r) => setResumen(r.data))
      .catch(() => {});
  }, [refresh]); // eslint-disable-line

  // Abre el modal e inicializa los inputs con los límites actuales
  const abrirModalCupos = () => {
    const init = {};
    SECCIONES.forEach((s) => {
      const c = resumen && (resumen.cupos || []).find((x) => x.seccion === s.key);
      init[s.key] = c ? c.limite : 0;
    });
    setLimitesEdit(init);
    setModalCupos(true);
  };

  // Guarda el máximo de lugares por sección (envía enteros, nunca strings/vacíos)
  const guardarLimites = () => {
    const limites = {};
    SECCIONES.forEach((s) => {
      limites[s.key] = Math.max(0, parseInt(limitesEdit[s.key], 10) || 0);
    });
    setSavingLimites(true);
    axios
      .put(`${BASE}/cupos`, { limites }, { headers })
      .then(() => {
        setSavingLimites(false);
        setModalCupos(false);
        Swal.fire({ icon: "success", title: "Límites guardados", timer: 1400, showConfirmButton: false });
        setRefresh((p) => p + 1);
      })
      .catch((err) => {
        setSavingLimites(false);
        if (err.response && err.response.status === 409) {
          const errs = (err.response.data.errores || [])
            .map((e) => {
              const sec = SECCIONES.find((s) => s.key === e.seccion);
              return `${sec ? sec.label : e.seccion}: intentaste ${e.intento} pero ya hay ${e.ocupados} ocupados`;
            })
            .join("<br/>");
          Swal.fire({ icon: "warning", title: "No se pudo guardar", html: errs || err.response.data.message });
        } else {
          Swal.fire({ icon: "error", title: "Error al guardar los límites" });
        }
      });
  };

  // Muestra el 409 de cupo insuficiente con el nombre de la sección
  const errorCupo = (err, fallback) => {
    if (err.response && err.response.status === 409) {
      const sec = SECCIONES.find((s) => s.key === err.response.data.seccion);
      Swal.fire({
        icon: "warning",
        title: "Sin cupo",
        text: `No hay lugares disponibles en ${sec ? sec.label : "la sección solicitada"}.`,
      });
    } else {
      Swal.fire({ icon: "error", title: fallback, text: err.response?.data?.message || "" });
    }
  };

  // ── Registrar reserva manual (admin) ──────────────────────────────────────
  const abrirModalManual = () => { setFManual(FORM_VACIO); setModalManual(true); };

  const guardarManual = () => {
    const totalLug = SECCIONES.reduce((t, s) => t + (parseInt(fManual.lugares[s.key], 10) || 0), 0);
    if (!fManual.nombre.trim() || !fManual.email.trim() || !fManual.telefono.trim()) {
      return Swal.fire({ icon: "warning", title: "Nombre, correo y teléfono son requeridos" });
    }
    if (totalLug < 1) {
      return Swal.fire({ icon: "warning", title: "Registra al menos un lugar" });
    }
    const body = {
      nombre: fManual.nombre.trim(),
      email: fManual.email.trim(),
      telefono: fManual.telefono.trim(),
      esAbrej: !!fManual.esAbrej,
      comentarios: fManual.comentarios.trim(),
      estado: fManual.estado,
      lugares: SECCIONES.reduce((a, s) => ({ ...a, [s.key]: parseInt(fManual.lugares[s.key], 10) || 0 }), {}),
    };
    // Monto opcional: si el admin lo escribe, se manda en centavos
    if (String(fManual.totalPesos).trim() !== "") {
      body.totalCentavos = Math.max(0, Math.round((parseFloat(fManual.totalPesos) || 0) * 100));
    }
    setSavingManual(true);
    axios.post(`${BASE}/manual`, body, { headers })
      .then(() => {
        setSavingManual(false);
        setModalManual(false);
        Swal.fire({ icon: "success", title: "Reserva registrada", timer: 1500, showConfirmButton: false });
        setRefresh((p) => p + 1);
      })
      .catch((err) => { setSavingManual(false); errorCupo(err, "Error al registrar la reserva"); });
  };

  // ── Editar reserva existente ──────────────────────────────────────────────
  const abrirModalEditar = (r) => {
    setFEditar({
      _id: r._id,
      folio: r.idReservaLF,
      nombre: r.nombre || "",
      email: r.email || "",
      telefono: r.telefono || "",
      esAbrej: !!r.esAbrej,
      comentarios: r.comentarios || "",
      estado: r.estado,
      totalPesos: String((r.totalCentavos || 0) / 100),
      lugares: SECCIONES.reduce((a, s) => ({ ...a, [s.key]: (r.lugares && r.lugares[s.key]) || 0 }), {}),
    });
    setModalEditar(true);
  };

  const guardarEditar = () => {
    const totalLug = SECCIONES.reduce((t, s) => t + (parseInt(fEditar.lugares[s.key], 10) || 0), 0);
    if (totalLug < 1) {
      return Swal.fire({ icon: "warning", title: "La reserva debe tener al menos un lugar" });
    }
    const body = {
      nombre: fEditar.nombre.trim(),
      email: fEditar.email.trim(),
      telefono: fEditar.telefono.trim(),
      esAbrej: !!fEditar.esAbrej,
      comentarios: fEditar.comentarios.trim(),
      estado: fEditar.estado,
      totalCentavos: Math.max(0, Math.round((parseFloat(fEditar.totalPesos) || 0) * 100)),
      lugares: SECCIONES.reduce((a, s) => ({ ...a, [s.key]: parseInt(fEditar.lugares[s.key], 10) || 0 }), {}),
    };
    setSavingEditar(true);
    axios.patch(`${BASE}/${fEditar._id}`, body, { headers })
      .then(() => {
        setSavingEditar(false);
        setModalEditar(false);
        Swal.fire({ icon: "success", title: "Reserva actualizada", timer: 1500, showConfirmButton: false });
        setRefresh((p) => p + 1);
      })
      .catch((err) => { setSavingEditar(false); errorCupo(err, "Error al actualizar la reserva"); });
  };

  const cancelar = (r) => {
    Swal.fire({
      title: "¿Cancelar reserva?",
      html: `<b>${r.nombre}</b> · ${r.totalLugares} lugar(es) · ${fmt(r.totalCentavos)}<br/><small>Libera el cupo. No emite reembolso automático.</small>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, cancelar",
      cancelButtonText: "No",
      confirmButtonColor: "#dc2626",
    }).then((res) => {
      if (!res.value) return;
      axios.patch(`${BASE}/${r._id}/cancelar`, {}, { headers })
        .then(() => {
          Swal.fire({ icon: "success", title: "Reserva cancelada", timer: 1400, showConfirmButton: false });
          setRefresh((p) => p + 1);
        })
        .catch((err) => Swal.fire({ icon: "error", title: "No se pudo cancelar", text: err.response?.data?.message || "" }));
    });
  };

  const exportExcel = () => {
    const sheetData = reservas.map((r) => ({
      Folio: r.idReservaLF || "",
      Fecha: moment(r.createdAt).format("YYYY-MM-DD HH:mm"),
      Nombre: r.nombre,
      Email: r.email,
      Telefono: r.telefono,
      Abrej: r.esAbrej ? "Sí" : "No",
      ...SECCIONES.reduce((a, s) => ({ ...a, [s.label]: (r.lugares && r.lugares[s.key]) || 0 }), {}),
      Lugares: r.totalLugares,
      Total: (r.totalCentavos || 0) / 100,
      Estado: ESTADO_LABEL[r.estado] || r.estado,
    }));
    const headersX = Object.keys(sheetData[0] || { Folio: "" });
    new ExportJsonExcel({
      fileName: "LugaresFiestas",
      datas: [{ sheetData, sheetName: "Reservas", sheetFilter: headersX, sheetHeader: headersX }],
    }).saveExcel();
  };

  const totalCobrado = resumen ? resumen.totalCentavos : 0;
  const cobradoTDC = resumen ? (resumen.tdcCentavos || 0) : 0;
  const cobradoEfectivo = resumen ? (resumen.efectivoCentavos || 0) : 0;
  const reservasEfectivo = resumen ? (resumen.reservasEfectivo || 0) : 0;
  const pendienteCobro = resumen ? (resumen.pendienteCentavos || 0) : 0;
  const reservasPagadas = resumen ? resumen.reservasPagadas : 0;
  const reservasPendientes = resumen ? (resumen.reservasPendientes || 0) : 0;
  // Lugares vendidos = todos los reservados (cualquier estado menos cancelada)
  const lugaresVendidos = resumen
    ? Object.values(resumen.lugaresPorSeccion).reduce((s, n) => s + n, 0)
    : 0;

  const cardKPIs = useMemo(() => ([
    {
      label: "Total cobrado", value: fmt(totalCobrado),
      sub: `TDC ${fmt(cobradoTDC)} · Efectivo ${fmt(cobradoEfectivo)}`,
      Icon: IcHandHoldingUsd, color: "#10b981", bg: "#f0fdf4",
    },
    { label: "Pendiente cobro", value: fmt(pendienteCobro), sub: `${reservasPendientes} pendiente${reservasPendientes === 1 ? "" : "s"}`, Icon: IcCheckCircle, color: "#f59e0b", bg: "#fffbeb" },
    { label: "Lugares vendidos", value: lugaresVendidos, sub: "reservados en total", Icon: IcTicket, color: "#3b82f6", bg: "#eff6ff" },
  ]), [totalCobrado, cobradoTDC, cobradoEfectivo, pendienteCobro, reservasPagadas, reservasEfectivo, reservasPendientes, lugaresVendidos]);

  if (!user) return null;
  if (!user.menu_lugaresFiestas) {
    return (
      <>
        <Header />
        <br />
        <div className="card container col-sm-11" style={{ padding: 48, textAlign: "center", color: "#94a3b8" }}>
          No tienes acceso al módulo Lugares Fiestas.
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <br />
      <div className="card container col-sm-11" style={{ paddingBottom: 32 }}>

        {/* Header */}
        <div className="page-header">
          <div>
            <h3 className="page-title">
              <IcTicket style={{ marginRight: 8, color: COLOR }} />
              Lugares Fiestas
            </h3>
            <p className="page-subtitle">Reservas de lugares — Rosh Hashaná / Yom Kipur</p>
          </div>
          <div className="page-actions" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button onClick={abrirModalManual}
              style={{ display: "flex", alignItems: "center", gap: 6, border: "none", background: "#0f172a",
                color: "#fff", borderRadius: 8, padding: "7px 12px", fontSize: "0.78rem", fontWeight: 600, cursor: "pointer" }}>
              <IcPlus size={15} /> Nueva reserva
            </button>
            <button onClick={() => setVerDesglose((v) => !v)}
              style={{ display: "flex", alignItems: "center", gap: 6, borderRadius: 8, padding: "7px 12px",
                fontSize: "0.78rem", fontWeight: 600, cursor: "pointer",
                border: `1px solid ${verDesglose ? COLOR : "#cbd5e1"}`,
                background: verDesglose ? COLOR : "#fff", color: verDesglose ? "#fff" : "#475569" }}>
              <IcTable size={15} /> Desglose
            </button>
            <button onClick={abrirModalCupos}
              style={{ display: "flex", alignItems: "center", gap: 6, border: "none", background: COLOR,
                color: "#fff", borderRadius: 8, padding: "7px 12px", fontSize: "0.78rem", fontWeight: 600, cursor: "pointer" }}>
              <IcTicket size={15} /> Cupos
            </button>
            <button onClick={exportExcel} disabled={!reservas.length}
              style={{ display: "flex", alignItems: "center", gap: 6, border: "none", background: "#10b981",
                color: "#fff", borderRadius: 8, padding: "7px 12px", fontSize: "0.78rem", fontWeight: 600,
                cursor: reservas.length ? "pointer" : "not-allowed", opacity: reservas.length ? 1 : 0.5 }}>
              <IcExcel size={15} /> Excel
            </button>
          </div>
        </div>

        {/* KPIs */}
        <div className="row mb-3">
          {cardKPIs.map((c, i) => (
            <div key={i} className="col-12 col-md-4 mb-2">
              <div style={{ background: "#fff", borderRadius: 14, padding: "16px 18px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.07)", borderTop: `3px solid ${c.color}`,
                display: "flex", alignItems: "center", gap: 14, height: "100%" }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: c.bg, flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <c.Icon size={18} style={{ color: c.color }} />
                </div>
                <div>
                  <div style={{ fontSize: "1.2rem", fontWeight: 800, color: "#0f172a", lineHeight: 1 }}>{c.value}</div>
                  <div style={{ fontSize: "0.67rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 4 }}>{c.label}</div>
                  <div style={{ fontSize: "0.72rem", color: c.color, fontWeight: 600, marginTop: 2 }}>{c.sub}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Cupo por sección (vista rápida solo-lectura; se edita en el modal "Cupos") */}
        {resumen && (
          <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: "14px 16px", marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between",
              flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
              <div style={{ fontWeight: 700, fontSize: "0.8rem", color: "#475569" }}>
                Cupo por sección (ocupados / límite)
              </div>
              {resumen.apartadosActivos > 0 && (
                <div style={{ fontSize: "0.7rem", color: "#d97706", fontWeight: 600 }}>
                  Incluye {resumen.apartadosActivos} pago{resumen.apartadosActivos === 1 ? "" : "s"} en proceso (ver abajo)
                </div>
              )}
            </div>
            <div className="row">
              {SECCIONES.map((sec) => {
                const c = (resumen.cupos || []).find((x) => x.seccion === sec.key) || { ocupados: 0, limite: 0 };
                const pct = c.limite > 0 ? Math.min(100, (c.ocupados / c.limite) * 100) : 0;
                return (
                  <div key={sec.key} className="col-6 col-md-4 mb-2">
                    <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "#334155", display: "flex", justifyContent: "space-between" }}>
                      <span>{sec.label}</span>
                      <span style={{ color: "#64748b" }}>{c.ocupados}/{c.limite}</span>
                    </div>
                    <div style={{ background: "#f1f5f9", borderRadius: 6, height: 6, marginTop: 3, overflow: "hidden" }}>
                      <div style={{ width: `${pct}%`, height: "100%", background: pct >= 100 ? "#dc2626" : COLOR, borderRadius: 6 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Filtros */}
        <div className="filter-bar" style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 12 }}>
          <div>
            <Label style={lbl}>ESTADO</Label>
            <Input bsSize="sm" type="select" value={estado} onChange={(e) => setEstado(e.target.value)} style={{ minWidth: 130 }}>
              <option value="">Todos</option>
              <option value="pagada">Pagada TDC</option>
              <option value="pagadaEfectivo">Pagada Efectivo</option>
              <option value="cortesia">Cortesía</option>
              <option value="pendiente">Pendiente</option>
              <option value="expirada">Expirada</option>
              <option value="cancelada">Cancelada</option>
            </Input>
          </div>
          <div>
            <Label style={lbl}>DESDE</Label>
            <Input bsSize="sm" type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} />
          </div>
          <div>
            <Label style={lbl}>HASTA</Label>
            <Input bsSize="sm" type="date" value={fin} onChange={(e) => setFin(e.target.value)} />
          </div>
          <div style={{ flexGrow: 1, minWidth: 160 }}>
            <Label style={lbl}>BUSCAR</Label>
            <Input bsSize="sm" type="text" placeholder="Nombre, email o teléfono" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
        </div>

        {/* Tabla */}
        <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 12 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.76rem", whiteSpace: "nowrap" }}>
            <thead>
              <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                {[
                  "Folio", "Fecha", "Nombre", "Teléfono", "Abrej",
                  ...(verDesglose ? SECCIONES.map((s) => s.abbr) : []),
                  "Lugares", "Total", "Estado", "",
                ].map((h, i) => (
                  <th key={i} style={{ padding: "8px 12px", textAlign: verDesglose && i >= 5 && i < 5 + SECCIONES.length ? "center" : "left",
                    color: "#475569", fontWeight: 700, whiteSpace: "nowrap",
                    fontSize: "0.64rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={verDesglose ? 15 : 9} style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}><Spinner size="sm" /> Cargando…</td></tr>
              ) : reservas.length === 0 ? (
                <tr><td colSpan={verDesglose ? 15 : 9} style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>Sin reservas</td></tr>
              ) : [
                // Apartados: pagos en curso, aún NO son reservas
                ...(!estado ? apartados.map((a) => (
                  <tr key={"h-" + a._id} style={{ borderBottom: "1px solid #f1f5f9", background: "#fffdf5" }}>
                    <td style={{ padding: "7px 12px", color: "#cbd5e1" }}>—</td>
                    <td style={{ padding: "7px 12px", color: "#64748b" }}>{moment(a.createdAt).format("YYYY-MM-DD")}</td>
                    <td style={{ padding: "7px 12px", color: "#64748b", fontWeight: 600, fontStyle: "italic" }}>{a.nombre}</td>
                    <td style={{ padding: "7px 12px", color: "#94a3b8" }}>{a.telefono}</td>
                    <td style={{ padding: "7px 12px", color: "#94a3b8" }}>{a.esAbrej ? "Sí" : "No"}</td>
                    {verDesglose && SECCIONES.map((s2) => {
                      const n = (a.lugares && a.lugares[s2.key]) || 0;
                      return <td key={s2.key} style={{ padding: "7px 12px", textAlign: "center", color: n ? "#64748b" : "#e2e8f0" }}>{n}</td>;
                    })}
                    <td style={{ padding: "7px 12px", textAlign: "center", color: "#64748b", fontWeight: 700 }}>{a.totalLugares}</td>
                    <td style={{ padding: "7px 12px", color: "#64748b" }}>{fmt(a.totalCentavos)}</td>
                    <td style={{ padding: "7px 12px" }}>
                      <span style={{ background: "#fef3c7", color: "#b45309", padding: "2px 10px", borderRadius: 20,
                        fontSize: "0.66rem", fontWeight: 700, whiteSpace: "nowrap" }}>
                        Pagando…
                      </span>
                      <div style={{ fontSize: "0.63rem", color: "#94a3b8", marginTop: 2, whiteSpace: "nowrap" }}>
                        {a.paso} · {a.minutosRestantes} min
                      </div>
                    </td>
                    <td style={{ padding: "7px 12px" }} />
                  </tr>
                )) : []),
                ...reservas.map((r) => {
                const est = ESTADO_STYLE[r.estado] || ESTADO_STYLE.expirada;
                return (
                  <tr key={r._id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "7px 12px", color: "#64748b" }}>{r.idReservaLF || "—"}</td>
                    <td style={{ padding: "7px 12px", color: "#64748b" }}>{moment(r.createdAt).format("YYYY-MM-DD")}</td>
                    <td style={{ padding: "7px 12px", color: "#0f172a", fontWeight: 600 }}>{r.nombre}</td>
                    <td style={{ padding: "7px 12px", color: "#475569" }}>{r.telefono}</td>
                    <td style={{ padding: "7px 12px", color: "#475569" }}>{r.esAbrej ? "Sí" : "No"}</td>
                    {verDesglose && SECCIONES.map((s) => {
                      const n = (r.lugares && r.lugares[s.key]) || 0;
                      return (
                        <td key={s.key} style={{ padding: "7px 12px", textAlign: "center",
                          color: n ? "#0f172a" : "#cbd5e1", fontWeight: n ? 600 : 400 }}>{n}</td>
                      );
                    })}
                    <td style={{ padding: "7px 12px", color: "#475569", textAlign: "center", fontWeight: 700 }}>{r.totalLugares}</td>
                    <td style={{ padding: "7px 12px", color: "#0f172a", fontWeight: 600 }}>{fmt(r.totalCentavos)}</td>
                    <td style={{ padding: "7px 12px" }}>
                      <span style={{ background: est.bg, color: est.color, padding: "2px 10px", borderRadius: 20,
                        fontSize: "0.68rem", fontWeight: 700, whiteSpace: "nowrap" }}>
                        {ESTADO_LABEL[r.estado] || r.estado}
                      </span>
                    </td>
                    <td style={{ padding: "7px 12px", whiteSpace: "nowrap" }}>
                      <button onClick={() => abrirModalEditar(r)} title="Editar reserva"
                        style={{ border: "none", background: "#e0e7ff", color: "#4f46e5", borderRadius: 8,
                          padding: "5px 8px", cursor: "pointer", display: "inline-flex", alignItems: "center", marginRight: 6 }}>
                        <IcEdit size={15} />
                      </button>
                      {(r.estado === "pagada" || r.estado === "pagadaEfectivo" || r.estado === "cortesia" || r.estado === "pendiente") && (
                        <button onClick={() => cancelar(r)} title="Cancelar reserva"
                          style={{ border: "none", background: "#fee2e2", color: "#dc2626", borderRadius: 8,
                            padding: "5px 8px", cursor: "pointer", display: "inline-flex", alignItems: "center" }}>
                          <IcBan size={15} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })]}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal: configurar máximo de lugares por sección */}
      <Modal isOpen={modalCupos} toggle={() => setModalCupos(false)}>
        <ModalHeader toggle={() => setModalCupos(false)}>
          Máximo de lugares por sección
        </ModalHeader>
        <ModalBody style={{ maxHeight: "68vh", overflowY: "auto" }}>
          <p style={{ fontSize: "0.76rem", color: "#64748b", marginTop: -4 }}>
            Define cuántos lugares hay disponibles en cada sección. No puede quedar por debajo de los lugares ya ocupados (pagados + reservas en curso).
          </p>
          {SECCIONES.map((sec) => {
            const c = (resumen && (resumen.cupos || []).find((x) => x.seccion === sec.key)) || { ocupados: 0, limite: 0 };
            const valor = parseInt(limitesEdit[sec.key], 10);
            const menorQueOcupados = !isNaN(valor) && valor < c.ocupados;
            return (
              <div key={sec.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                gap: 12, padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: "0.82rem", fontWeight: 600, color: "#334155" }}>{sec.label}</div>
                  <div style={{ fontSize: "0.68rem", color: "#94a3b8" }}>Ocupados: {c.ocupados}</div>
                  {menorQueOcupados && (
                    <div style={{ fontSize: "0.66rem", color: "#dc2626" }}>No puede ser menor a {c.ocupados}</div>
                  )}
                </div>
                <Input
                  bsSize="sm"
                  type="number"
                  min="0"
                  value={limitesEdit[sec.key] === undefined ? "" : limitesEdit[sec.key]}
                  onChange={(e) => { const v = e.target.value; setLimitesEdit((p) => ({ ...p, [sec.key]: v })); }}
                  invalid={menorQueOcupados}
                  style={{ width: 100, textAlign: "center", flexShrink: 0 }}
                />
              </div>
            );
          })}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" size="sm" onClick={() => setModalCupos(false)}>Cancelar</Button>
          <Button color="success" size="sm" onClick={guardarLimites} disabled={savingLimites}>
            {savingLimites ? "Guardando…" : "Guardar límites"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal: registrar reserva manual (admin) */}
      <Modal isOpen={modalManual} toggle={() => setModalManual(false)} size="lg">
        <ModalHeader toggle={() => setModalManual(false)}>Nueva reserva (registro manual)</ModalHeader>
        <ModalBody style={{ maxHeight: "68vh", overflowY: "auto" }}>
          <p style={{ fontSize: "0.76rem", color: "#64748b", marginTop: -4 }}>
            Para registrar reservas que no pasan por el formulario público (pago en efectivo, transferencia, invitación).
          </p>
          <ReservaForm f={fManual} setF={setFManual} />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" size="sm" onClick={() => setModalManual(false)}>Cancelar</Button>
          <Button color="success" size="sm" onClick={guardarManual} disabled={savingManual}>
            {savingManual ? "Guardando…" : "Registrar reserva"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal: editar reserva existente */}
      <Modal isOpen={modalEditar} toggle={() => setModalEditar(false)} size="lg">
        <ModalHeader toggle={() => setModalEditar(false)}>
          Editar reserva {fEditar && fEditar.folio ? `· Folio #${fEditar.folio}` : ""}
        </ModalHeader>
        <ModalBody style={{ maxHeight: "68vh", overflowY: "auto" }}>
          {fEditar && <ReservaForm f={fEditar} setF={setFEditar} editando />}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" size="sm" onClick={() => setModalEditar(false)}>Cancelar</Button>
          <Button color="success" size="sm" onClick={guardarEditar} disabled={savingEditar}>
            {savingEditar ? "Guardando…" : "Guardar cambios"}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

// ── Formulario compartido por los modales de crear/editar reserva ────────────
function ReservaForm({ f, setF, editando }) {
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setLugar = (key, v) =>
    setF((p) => ({ ...p, lugares: { ...p.lugares, [key]: v } }));
  const totalLug = SECCIONES.reduce((t, s) => t + (parseInt(f.lugares[s.key], 10) || 0), 0);

  return (
    <>
      <div className="row">
        <div className="col-12 col-md-4 mb-2">
          <Label style={lbl}>NOMBRE *</Label>
          <Input bsSize="sm" value={f.nombre} onChange={(e) => { const v = e.target.value; set("nombre", v); }} />
        </div>
        <div className="col-12 col-md-4 mb-2">
          <Label style={lbl}>CORREO *</Label>
          <Input bsSize="sm" type="email" value={f.email} onChange={(e) => { const v = e.target.value; set("email", v); }} />
        </div>
        <div className="col-12 col-md-4 mb-2">
          <Label style={lbl}>TELÉFONO *</Label>
          <Input bsSize="sm" value={f.telefono} onChange={(e) => { const v = e.target.value; set("telefono", v); }} />
        </div>
      </div>

      <div className="row">
        <div className="col-6 col-md-4 mb-2">
          <Label style={lbl}>ESTADO</Label>
          <Input bsSize="sm" type="select" value={f.estado}
            onChange={(e) => { const v = e.target.value; set("estado", v); }}>
            <option value="pagada">Pagada TDC</option>
            <option value="pagadaEfectivo">Pagada Efectivo</option>
            <option value="cortesia">Cortesía</option>
            <option value="pendiente">Pendiente</option>
            {editando && <option value="expirada">Expirada</option>}
            {editando && <option value="cancelada">Cancelada</option>}
          </Input>
        </div>
        <div className="col-6 col-md-4 mb-2">
          <Label style={lbl}>MONTO (MXN)</Label>
          <Input bsSize="sm" type="number" min="0" step="any" placeholder={editando ? "" : "automático"}
            value={f.totalPesos} onChange={(e) => { const v = e.target.value; set("totalPesos", v); }} />
        </div>
        <div className="col-12 col-md-4 mb-2" style={{ display: "flex", alignItems: "flex-end", paddingBottom: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input type="checkbox" checked={!!f.esAbrej}
              onChange={(e) => { const v = e.target.checked; set("esAbrej", v); }} />
            <span style={{ fontSize: "0.78rem", fontWeight: 600, color: "#475569" }}>¿Es Abrej?</span>
          </div>
        </div>
      </div>

      <div style={{ fontWeight: 700, fontSize: "0.72rem", color: "#475569", textTransform: "uppercase",
        letterSpacing: "0.05em", margin: "10px 0 6px" }}>
        Lugares · {totalLug} en total
      </div>
      <div className="row">
        {SECCIONES.map((sec) => (
          <div key={sec.key} className="col-6 col-md-4 mb-2">
            <Label style={lbl}>{sec.label.toUpperCase()}</Label>
            <Input bsSize="sm" type="number" min="0" value={f.lugares[sec.key]}
              onChange={(e) => { const v = e.target.value; setLugar(sec.key, v); }} />
          </div>
        ))}
      </div>

      <div className="mt-2">
        <Label style={lbl}>COMENTARIOS</Label>
        <Input bsSize="sm" type="textarea" rows={2} value={f.comentarios}
          onChange={(e) => { const v = e.target.value; set("comentarios", v); }} />
      </div>
    </>
  );
}

const lbl = { fontSize: "0.72rem", fontWeight: 600, color: "#64748b", marginBottom: 3, display: "block" };

export default ListadoLugaresFiestas;
